import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

/**
 * Root-level error boundary to catch unhandled errors and prevent white screens
 * Shows user-friendly error UI with recovery options
 */
export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false
  };

  public static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error details for debugging
    console.error('Uncaught error:', error, errorInfo);

    // TODO: Send to error reporting service (Sentry, LogRocket, etc.)
    // if (import.meta.env.PROD) {
    //   Sentry.captureException(error, { extra: errorInfo });
    // }

    this.setState({ errorInfo });
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  private handleGoHome = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
    window.location.href = '/';
  };

  public render() {
    if (this.state.hasError) {
      // Custom fallback UI provided by parent
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="min-h-screen bg-ink-50 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-danger-bg rounded-full">
                <AlertTriangle className="h-12 w-12 text-danger-fg" />
              </div>
            </div>

            <h1 className="text-2xl font-bold text-ink-900 text-center mb-4">
              Something went wrong
            </h1>

            <p className="text-ink-500 text-center mb-6">
              We're sorry for the inconvenience. The application encountered an unexpected error.
            </p>

            {this.state.error && (
              <div className="mb-6 p-4 bg-ink-100 rounded-lg">
                <p className="text-sm text-ink-700 font-medium mb-1">Error:</p>
                <p className="text-sm text-ink-500">{this.state.error.message}</p>
              </div>
            )}

            <div className="space-y-3">
              <button
                onClick={this.handleReset}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-navy-600 text-white rounded-lg hover:bg-navy-700 transition-colors font-medium"
              >
                <RefreshCw size={20} />
                Try Again
              </button>

              <button
                onClick={this.handleGoHome}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 border border-ink-300 text-ink-700 rounded-lg hover:bg-ink-50 transition-colors font-medium"
              >
                <Home size={20} />
                Return Home
              </button>
            </div>

            {import.meta.env.DEV && this.state.error && (
              <details className="mt-6">
                <summary className="text-sm text-ink-500 cursor-pointer hover:text-ink-900 font-medium">
                  Technical Details (Development Only)
                </summary>
                <div className="mt-3 space-y-3">
                  <div>
                    <p className="text-xs font-semibold text-ink-700 mb-1">Stack Trace:</p>
                    <pre className="text-xs bg-ink-900 text-ink-100 p-3 rounded overflow-auto max-h-48">
                      <code>{this.state.error.stack}</code>
                    </pre>
                  </div>
                  {this.state.errorInfo && (
                    <div>
                      <p className="text-xs font-semibold text-ink-700 mb-1">Component Stack:</p>
                      <pre className="text-xs bg-ink-900 text-ink-100 p-3 rounded overflow-auto max-h-48">
                        <code>{this.state.errorInfo.componentStack}</code>
                      </pre>
                    </div>
                  )}
                </div>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
